import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge, SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import { useBranding } from '../lib/branding.tsx';
import { companyInfoLabel, stationOptions } from '../lib/companyInfo.ts';
import type {
  AdminSubmission,
  Company,
  CompanyDocument,
  Question,
  Submission,
} from '../lib/types.ts';
import { computeProgress } from '../lib/visibility.ts';

export function AdminCompanyDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const companyQuery = useQuery({
    queryKey: ['company', id],
    queryFn: () => adminApi.get<{ company: Company }>(`/api/companies/${id}`),
    enabled: !!id,
  });
  const submissionsQuery = useQuery({
    queryKey: ['company', id, 'submissions'],
    queryFn: () => adminApi.get<{ submissions: AdminSubmission[] }>(`/api/companies/${id}/submissions`),
    enabled: !!id,
  });
  const questionsQuery = useQuery({
    queryKey: ['questions'],
    queryFn: () => adminApi.get<{ questions: Question[] }>('/api/questions'),
  });
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });

  const inviteMutation = useMutation({
    mutationFn: () => adminApi.post(`/api/companies/${id}/invite`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company', id] }),
  });

  const approveAllMutation = useMutation({
    mutationFn: () => adminApi.post(`/api/companies/${id}/review/approve-all`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company', id] });
      void qc.invalidateQueries({ queryKey: ['company', id, 'submissions'] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminApi.delete(`/api/companies/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['companies'] });
      navigate('/admin/companies', { replace: true });
    },
  });

  const documents = documentsQuery.data?.documents ?? [];
  const docMap = useMemo(() => {
    const m = new Map<string, CompanyDocument>();
    for (const d of documents) m.set(d.id, d);
    return m;
  }, [documents]);

  const submissions = submissionsQuery.data?.submissions ?? [];
  const questions = questionsQuery.data?.questions ?? [];
  const subByQ = useMemo(() => {
    const m = new Map<string, AdminSubmission>();
    for (const s of submissions) m.set(s.question_id, s);
    return m;
  }, [submissions]);

  const progress = useMemo(
    () => computeProgress(questions, submissions as unknown as Submission[]),
    [questions, submissions],
  );

  if (companyQuery.isLoading || submissionsQuery.isLoading || questionsQuery.isLoading) {
    return <CenteredSpinner />;
  }
  const company = companyQuery.data?.company;
  if (!company) {
    return <ErrorMessage error={new Error('Company not found')} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/admin/companies" className="text-sm text-slate-500 hover:text-slate-700">
            ← Companies
          </Link>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">{company.company_name}</h1>
          <div className="mt-1 flex items-center gap-3 text-sm text-slate-500">
            <CompanyStatusBadge status={company.status} />
            <span>{company.contact_name}</span>
            <span>·</span>
            <span>{company.email}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Btn
            variant="secondary"
            onClick={() => inviteMutation.mutate()}
            loading={inviteMutation.isPending}
          >
            {company.invited_at ? 'Resend invite' : 'Send invite'}
          </Btn>
          {(company.status === 'review' || company.status === 'changes') && (
            <Btn
              onClick={() => approveAllMutation.mutate()}
              loading={approveAllMutation.isPending}
            >
              Approve all
            </Btn>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Progress</div>
          <div className="mt-2"><ProgressBar value={company.progress} /></div>
          <div className="mt-1 text-xs text-slate-500">
            {progress.completedRequired} / {progress.totalRequired} required complete
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Invited</div>
          <div className="mt-2 text-sm text-slate-700">
            {company.invited_at ? new Date(company.invited_at).toLocaleString() : '—'}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Compliant until</div>
          <div className="mt-2 text-sm text-slate-700">
            {company.expires_at ? new Date(company.expires_at).toLocaleDateString() : '—'}
          </div>
        </div>
      </div>

      <CompanyInfoPanel company={company} />

      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          Submissions
        </h2>
        <div className="space-y-3">
          {questions
            .filter((q) => progress.visibleIds.has(q.id))
            .map((q) => {
              const sub = subByQ.get(q.id);
              return (
                <SubmissionCard
                  key={q.id}
                  question={q}
                  submission={sub}
                  documentName={(docId) => docMap.get(docId)?.name ?? docId.slice(0, 8)}
                  companyId={id}
                />
              );
            })}
          {questions.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              No questions defined yet.
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-red-600">Danger zone</h3>
        <div className="mt-3 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="text-sm text-red-700">
            Delete this company and all submitted data. This cannot be undone.
          </div>
          <Btn
            variant="danger"
            onClick={() => {
              if (confirm(`Delete ${company.company_name}? This cannot be undone.`)) {
                deleteMutation.mutate();
              }
            }}
            loading={deleteMutation.isPending}
          >
            Delete company
          </Btn>
        </div>
      </div>
    </div>
  );
}

function SubmissionCard({
  question,
  submission,
  documentName,
  companyId,
}: {
  question: Question;
  submission: AdminSubmission | undefined;
  documentName: (id: string) => string;
  companyId: string;
}) {
  const qc = useQueryClient();
  const [reviewMode, setReviewMode] = useState<null | 'reject'>(null);
  const [rejectComment, setRejectComment] = useState('');

  const reviewMutation = useMutation({
    mutationFn: (input: { decision: 'approve' | 'reject'; comment?: string }) =>
      adminApi.post(
        `/api/companies/${companyId}/submissions/${submission?.id}/review`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company', companyId, 'submissions'] });
      void qc.invalidateQueries({ queryKey: ['company', companyId] });
      setReviewMode(null);
      setRejectComment('');
    },
  });

  const downloadFile = async (fileId: string) => {
    const res = await adminApi.get<{ url: string }>(
      `/api/companies/${companyId}/submissions/${submission?.id}/files/${fileId}/download-url`,
    ).catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900">{question.label}</div>
          {question.help_text && (
            <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
          )}
        </div>
        {submission && <SubmissionStatusBadge status={submission.status} />}
      </div>

      <div className="mt-3 text-sm text-slate-700">
        <AnswerPreview question={question} submission={submission} documentName={documentName} />
      </div>

      {submission?.submission_files && submission.submission_files.length > 0 && (
        <div className="mt-3 space-y-1">
          {submission.submission_files.map((f) => (
            <button
              key={f.id}
              onClick={() => void downloadFile(f.id)}
              className="block text-left text-xs text-blue-600 hover:underline"
            >
              📎 {f.file_name}
            </button>
          ))}
        </div>
      )}

      {submission?.reviewer_comment && (
        <div className="mt-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
          <div className="font-medium">Reviewer note</div>
          <div>{submission.reviewer_comment}</div>
        </div>
      )}

      {submission && reviewMode !== 'reject' && (
        <div className="mt-3 flex gap-2">
          <Btn
            size="sm"
            variant="secondary"
            onClick={() => reviewMutation.mutate({ decision: 'approve' })}
            loading={reviewMutation.isPending && reviewMutation.variables?.decision === 'approve'}
          >
            Approve
          </Btn>
          <Btn size="sm" variant="ghost" onClick={() => setReviewMode('reject')}>
            Request changes
          </Btn>
        </div>
      )}

      {reviewMode === 'reject' && (
        <div className="mt-3 space-y-2">
          <Field label="Comment for the company" required>
            <TextArea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Explain what needs to change…"
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Btn size="sm" variant="ghost" onClick={() => setReviewMode(null)}>Cancel</Btn>
            <Btn
              size="sm"
              variant="danger"
              disabled={!rejectComment.trim()}
              loading={reviewMutation.isPending}
              onClick={() =>
                reviewMutation.mutate({ decision: 'reject', comment: rejectComment.trim() })
              }
            >
              Send request
            </Btn>
          </div>
        </div>
      )}
      {reviewMutation.error && (
        <div className="mt-2"><ErrorMessage error={reviewMutation.error} /></div>
      )}
    </div>
  );
}

function AnswerPreview({
  question,
  submission,
  documentName,
}: {
  question: Question;
  submission: AdminSubmission | undefined;
  documentName: (id: string) => string;
}) {
  if (!submission) return <span className="italic text-slate-400">Not answered yet</span>;
  const a = submission.answer;
  if (question.type === 'signature') {
    if (
      a &&
      typeof a === 'object' &&
      !Array.isArray(a) &&
      typeof (a as { signature_data_url?: unknown }).signature_data_url === 'string'
    ) {
      const sig = a as { signature_data_url: string; signer_name: string; signed_at: string };
      return (
        <div className="space-y-1">
          <img
            src={sig.signature_data_url}
            alt="Signature"
            className="h-24 rounded border border-slate-200 bg-white object-contain"
          />
          <div className="text-sm text-slate-700">
            Signed by <span className="font-medium">{sig.signer_name}</span>
          </div>
          <div className="text-xs text-slate-500">
            {new Date(sig.signed_at).toLocaleString()}
          </div>
        </div>
      );
    }
    return <span className="italic text-slate-400">Not signed yet</span>;
  }
  if (question.type === 'document_adopt') {
    if (submission.submission_adoptions.length === 0) {
      return <span className="italic text-slate-400">No adoption recorded</span>;
    }
    return (
      <ul className="space-y-1">
        {submission.submission_adoptions.map((ad) => (
          <li key={ad.id} className="text-sm">
            {ad.mode === 'adopted' ? '✓ Adopted: ' : '↑ Uploaded: '}
            {documentName(ad.document_id)}
          </li>
        ))}
      </ul>
    );
  }
  if (Array.isArray(a)) return <span>{(a as string[]).join(', ')}</span>;
  if (typeof a === 'boolean') return <span>{a ? 'Yes' : 'No'}</span>;
  if (a === null || a === undefined || a === '') {
    return <span className="italic text-slate-400">Not answered yet</span>;
  }
  return <span>{String(a)}</span>;
}

// ----------------------------------------------------------------------
// Company information panel (admin edits the pre-filled fields; read-only
// view of what the company has filled in themselves).
// ----------------------------------------------------------------------

type PrefillForm = {
  contractor_type: string;
  vendor_number: string;
  contract_number: string;
  cluster_region: string;
  station: string;
};

function toPrefillForm(c: Company): PrefillForm {
  return {
    contractor_type: c.contractor_type ?? '',
    vendor_number: c.vendor_number ?? '',
    contract_number: c.contract_number ?? '',
    cluster_region: c.cluster_region ?? '',
    station: c.station ?? '',
  };
}

function CompanyInfoPanel({ company }: { company: Company }) {
  const qc = useQueryClient();
  const { branding } = useBranding();
  const stations = stationOptions(branding);
  const [form, setForm] = useState<PrefillForm>(() => toPrefillForm(company));
  const [error, setError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setForm(toPrefillForm(company));
  }, [company.id, company.updated_at]);

  const dirty =
    form.contractor_type !== (company.contractor_type ?? '') ||
    form.vendor_number !== (company.vendor_number ?? '') ||
    form.contract_number !== (company.contract_number ?? '') ||
    form.cluster_region !== (company.cluster_region ?? '') ||
    form.station !== (company.station ?? '');

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, string | null> = {};
      for (const key of [
        'contractor_type',
        'vendor_number',
        'contract_number',
        'cluster_region',
        'station',
      ] as const) {
        const trimmed = form[key].trim();
        payload[key] = trimmed.length === 0 ? null : trimmed;
      }
      return adminApi.patch<{ company: Company }>(
        `/api/companies/${company.id}`,
        payload,
      );
    },
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      void qc.invalidateQueries({ queryKey: ['company', company.id] });
    },
    onError: (err) => setError(err),
  });

  function set<K extends keyof PrefillForm>(key: K, value: PrefillForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Company information
        </h2>
        <div className="flex items-center gap-2">
          {savedAt && !dirty && (
            <span className="text-xs text-emerald-600">Saved</span>
          )}
          <Btn
            size="sm"
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={!dirty}
          >
            Save pre-filled
          </Btn>
        </div>
      </div>

      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
        Pre-filled by admin
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={companyInfoLabel(branding, 'contractorType')}>
          <TextInput
            value={form.contractor_type}
            onChange={(e) => set('contractor_type', e.target.value)}
          />
        </Field>
        <Field label={companyInfoLabel(branding, 'vendorNumber')}>
          <TextInput
            value={form.vendor_number}
            onChange={(e) => set('vendor_number', e.target.value)}
          />
        </Field>
        <Field label={companyInfoLabel(branding, 'contractNumber')}>
          <TextInput
            value={form.contract_number}
            onChange={(e) => set('contract_number', e.target.value)}
          />
        </Field>
        <Field label={companyInfoLabel(branding, 'clusterRegion')}>
          <TextInput
            value={form.cluster_region}
            onChange={(e) => set('cluster_region', e.target.value)}
          />
        </Field>
        <Field
          label={companyInfoLabel(branding, 'station')}
          hint={
            stations.length === 0
              ? 'Add stations in Settings to enable this dropdown.'
              : undefined
          }
        >
          <Select
            value={form.station}
            onChange={(e) => set('station', e.target.value)}
            disabled={stations.length === 0}
          >
            <option value="">— Select —</option>
            {stations.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
            {form.station && !stations.includes(form.station) && (
              <option value={form.station}>{form.station} (legacy)</option>
            )}
          </Select>
        </Field>
      </div>

      <div className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
        Filled by company
      </div>
      <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <ReadOnlyRow label="ABN" value={company.abn} />
        <ReadOnlyRow label="Trustee Name and ACN" value={company.trustee_info} />
        <ReadOnlyRow
          label={companyInfoLabel(branding, 'primaryContact')}
          value={company.primary_contact_name}
        />
        <ReadOnlyRow label="Contact role/position" value={company.contact_role} />
        <ReadOnlyRow label="Contact phone" value={company.contact_phone} />
        <ReadOnlyRow label="Contact email" value={company.contact_email} />
        <ReadOnlyRow
          label="# of employees"
          value={company.employee_count === null ? null : String(company.employee_count)}
        />
      </dl>

      <ErrorMessage error={error} />
    </div>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">
        {value && value.trim().length > 0 ? (
          value
        ) : (
          <span className="italic text-slate-400">Not provided</span>
        )}
      </dd>
    </div>
  );
}
